import { clients } from '@moovly/clients';
import React from 'react';
import { Project } from '@moovly/clients/lib/resources/projects/projects.interfaces';
import useCurrentUser from '../../../hooks/useCurrentUser';
import useUser from './useUser';

type groupSelectOption = { value: string; label: string };

const useUserProjects = (
  userUuid: string
): {
  projects: Project[];
  count: number;
  groups: { value: string; label: string }[];
  onGroupChange: (value: string) => void;
  loading: boolean;
  currentGroupId: string;
  loadMoreProjects: () => void;
  allGroupsWithAccess: groupSelectOption[];
} => {
  const { user } = useUser(userUuid);
  const { groupsWithAdminRight } = useCurrentUser();
  const [currentGroupId, setCurrentGroupId] = React.useState(
    (groupsWithAdminRight && groupsWithAdminRight.length && groupsWithAdminRight[0].id) || null
  );
  const [projects, setProjects] = React.useState<Project[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [page, setPage] = React.useState<number>(1);
  const [count, setCount] = React.useState<number>(1);
  const groups = React.useMemo(
    () => groupsWithAdminRight.map((group) => ({ value: group.id, label: group.name })),
    [groupsWithAdminRight]
  );

  const allGroupsWithAccessTransformed = user?.memberships?.map(
    (membership) => ({ value: membership.group?.id, label: membership.group?.name })
  ) || [];

  const loadProjects = React.useCallback(
    (groupId: string, page: number) => {
      setLoading(true);
      clients.Projects.getUserProjectsById(
        userUuid,
        {
          page: page,
          pageSize: 20,
          filter: 'all',
        },
        groupId
      )
        .then((data) => {
          setPage(page);
          if (page === 1) setProjects((v) => [...data.results]);
          else setProjects((v) => [...v!, ...data.results]);
          setCount(data.count);
          setLoading(false);
        })
        .catch((error: any) => setLoading(false));
    },
    [userUuid]
  );

  const loadMoreProjects = React.useCallback(() => {
    if (!!currentGroupId) loadProjects(currentGroupId, page + 1);
  }, [page, loadProjects, currentGroupId]);

  React.useEffect(() => {
    if (!!currentGroupId) {
      setLoading(true);
      loadProjects(currentGroupId, 1);
      setPage(1);
    }
  }, [currentGroupId, loadProjects]);

  return {
    projects,
    count,
    loading,
    loadMoreProjects,
    groups,
    onGroupChange: setCurrentGroupId,
    currentGroupId: currentGroupId!,
    allGroupsWithAccess: allGroupsWithAccessTransformed as groupSelectOption[],
  };
};

export default useUserProjects;
